import React, { useEffect, useState } from 'react'
import { Document, EditIcon, DeleteIcon } from '../../../assests/ExportImageIcon'
import Modal from '../../../componants/modal/Modal'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import './kycDocument.css'

const EMPTY_UPLOAD_FORM = { document_type_id: '', document_number: '', notes: '' }

const WHY_KYC = [
    'Comply with regulatory requirements',
    'Prevent fraud and identity theft',
    'Enable secure transactions',
    'Unlock premium features',
]

const GUIDELINES = [
    'Documents must be clear and readable',
    'All corners of the document should be visible',
    'File size should not exceed 5MB',
    'Accepted formats: JPG, PNG, PDF',
    'Documents should be valid and not expired',
]

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const formatDate = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return `Uploaded on ${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
}

const normalizeDocument = (doc = {}) => ({
    id: doc.id ?? doc.document_id,
    typeId: doc.document_type_id ?? doc.document_type?.id ?? null,
    title: doc.document_type_label || doc.document_type?.name || doc.title || doc.name || 'Document',
    number: doc.document_number || doc.number || '',
    fileUrl: doc.file || doc.file_url || doc.document || doc.url || '',
    downloadUrl: doc.download_url || doc.file || doc.file_url || doc.document || doc.url || '',
    date: formatDate(doc.created_at || doc.uploaded_at),
    raw: doc,
})

const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ''))

const KycDocument = ({ onBack }) => {
    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(true)
    const [documentTypes, setDocumentTypes] = useState([])
    const [viewDoc, setViewDoc] = useState(null)
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD_FORM)
    const [uploadFile, setUploadFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [editDoc, setEditDoc] = useState(null)
    const [deleteDoc, setDeleteDoc] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [downloadingId, setDownloadingId] = useState(null)

    const loadDocuments = () => {
        setLoading(true)
        service.get(endpoints.users.documents, {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data
                const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : []
                setDocuments(list.map(normalizeDocument))
            })
            .catch(() => {
                setDocuments([])
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        loadDocuments()

        service.get(endpoints.users.documentTypes, {}, { silent: true })
            .then(({ data }) => {
                setDocumentTypes(Array.isArray(data?.data) ? data.data : [])
            })
            .catch(() => {
                setDocumentTypes([])
            })
    }, [])

    const openUploadModal = (typeId) => {
        setEditDoc(null)
        setUploadForm({ ...EMPTY_UPLOAD_FORM, document_type_id: typeId ? String(typeId) : '' })
        setUploadFile(null)
        setUploadModalOpen(true)
    }

    const openEditModal = (doc) => {
        setEditDoc(doc)
        setUploadForm({
            document_type_id: doc.typeId ? String(doc.typeId) : '',
            document_number: doc.number || '',
            notes: doc.raw?.notes || '',
        })
        setUploadFile(null)
        setUploadModalOpen(true)
    }

    const closeUploadModal = () => {
        if (uploading) return
        setUploadModalOpen(false)
        setEditDoc(null)
    }

    const handleUploadFieldChange = (field) => (e) => {
        setUploadForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

    const handleUploadSubmit = async (e) => {
        e.preventDefault()
        if (uploading) return
        if (!uploadForm.document_type_id) {
            showToast('Document type is required', 'error')
            return
        }
        if (!editDoc && !uploadFile) {
            showToast('File is required', 'error')
            return
        }

        setUploading(true)
        try {
            const payload = new FormData()
            payload.append('document_type_id', uploadForm.document_type_id)
            if (uploadFile) payload.append('file', uploadFile)
            if (uploadForm.document_number) payload.append('document_number', uploadForm.document_number)
            if (uploadForm.notes) payload.append('notes', uploadForm.notes)

            if (editDoc) {
                const url = endpoints.users.documentDetail.replace('{document_id}', editDoc.id)
                const { data } = await service.patchForm(url, payload)
                showToast(data?.message || 'Document updated successfully', 'success')
            } else {
                const { data } = await service.upload(endpoints.users.documents, payload)
                showToast(data?.message || 'Document uploaded successfully', 'success')
            }
            setUploadModalOpen(false)
            setEditDoc(null)
            loadDocuments()
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setUploading(false)
        }
    }

    const handleDownload = async (doc) => {
        if (!doc.id || downloadingId) return
        setDownloadingId(doc.id)
        try {
            const url = endpoints.users.documentsDownload.replace('{document_id}', doc.id)
            const response = await service.download(url)

            const disposition = response.headers?.['content-disposition'] || ''
            const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
            const fallbackExt = (doc.downloadUrl || doc.fileUrl || '').split('.').pop().split('?')[0]
            const filename = nameMatch
                ? decodeURIComponent(nameMatch[1])
                : `${doc.title}${fallbackExt ? `.${fallbackExt}` : ''}`

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch {
            showToast('Failed to download document', 'error')
        } finally {
            setDownloadingId(null)
        }
    }

    const handleDeleteConfirm = async () => {
        if (!deleteDoc || deleting) return
        setDeleting(true)
        try {
            const url = endpoints.users.documentDetail.replace('{document_id}', deleteDoc.id)
            const { data } = await service.delete(url)
            showToast(data?.message || 'Document deleted successfully', 'success')
            setDeleteDoc(null)
            loadDocuments()
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className='kycDoc'>
            <div className='tripDocHeader' onClick={onBack}>
                <BackArrow />
                <span className='bsSectionTitle'>Documents</span>
            </div>

            <div className='bsSectionTitle mt-3'>Uploaded Documents</div>
            {loading ? (
                <p className='securityMenuItem__subtitle mt-2'>Loading documents...</p>
            ) : documents.length === 0 ? (
                <p className='securityMenuItem__subtitle mt-2'>No documents uploaded yet.</p>
            ) : (
                <div className='kycDocGrid'>
                    {documents.map((doc) => (
                        <div className='kycDocCard bgWhite commonBorder borderRadius20 paddingCommon20' key={doc.id}>
                            <div className='kycDocCard__actions'>
                                <button
                                    type='button'
                                    className='kycDocCard__actionBtn'
                                    onClick={() => openEditModal(doc)}
                                    aria-label='Edit document'
                                    title='Edit document'
                                >
                                    <img src={EditIcon} alt="" />
                                </button>
                                <button
                                    type='button'
                                    className='kycDocCard__actionBtn kycDocCard__actionBtn--danger'
                                    onClick={() => setDeleteDoc(doc)}
                                    aria-label='Delete document'
                                    title='Delete document'
                                >
                                    <img src={DeleteIcon} alt="" />
                                </button>
                            </div>
                            <div className='kycDocCard__head'>
                                <span className='kycDocCard__icon'>
                                    <img src={Document} alt="" />
                                </span>
                                <div>
                                    <div className='kycDocCard__title'>{doc.title}</div>
                                    {doc.number && <div className='kycDocCard__number'>{doc.number}</div>}
                                    {doc.date && <div className='kycDocCard__date'>{doc.date}</div>}
                                </div>
                            </div>
                            <div className='bookingConfirmActions'>
                                <button type='button' className='bookingDownloadBtn' onClick={() => setViewDoc(doc)}>View</button>
                                {doc.id && (
                                    <button
                                        type='button'
                                        className='bookingDownloadBtn'
                                        onClick={() => handleDownload(doc)}
                                        disabled={downloadingId === doc.id}
                                    >
                                        {downloadingId === doc.id ? 'Downloading...' : 'Download'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className='bsSectionTitle mt-4'>Add More Documents</div>
            <div className='kycDocGrid'>
                {documentTypes
                    .filter((type) => !documents.some((doc) => doc.typeId === type.id))
                    .map((type) => (
                        <div className='kycDocCard bgWhite commonBorder borderRadius20 paddingCommon20' key={type.id}>
                            <div className='kycDocCard__head kycDocCard__head--center'>
                                <span className='kycDocCard__icon'>
                                    <img src={Document} alt="" />
                                </span>
                                <div className='kycDocCard__title'>{type.name}</div>
                            </div>
                            <button type='button' className='travellerAddBtn kycDocUploadBtn' onClick={() => openUploadModal(type.id)}>
                                Upload
                            </button>
                        </div>
                    ))}
            </div>

            <div className='bsSectionTitle mt-4'>Why KYC is Required?</div>
            <div className='bsBox'>
                <ul className='bsInfoList'>
                    {WHY_KYC.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            </div>

            <div className='bsSectionTitle mt-4'>Document Guidelines</div>
            <div className='bsBox'>
                <ul className='bsInfoList'>
                    {GUIDELINES.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            </div>

            <Modal open={!!viewDoc} onClose={() => setViewDoc(null)} maxWidth={520}>
                <div className='kycDocModal'>
                    <div className='kycDocModal__title'>{viewDoc?.title}</div>
                    {viewDoc?.number && <div className='kycDocCard__number mb-2'>{viewDoc.number}</div>}
                    {!viewDoc?.fileUrl ? (
                        <p className='securityMenuItem__subtitle'>No preview available for this document.</p>
                    ) : isPdfUrl(viewDoc.fileUrl) ? (
                        <a href={viewDoc.fileUrl} target='_blank' rel='noreferrer' className='commonBtn w-100'>Open PDF</a>
                    ) : (
                        <img src={viewDoc.fileUrl} alt={viewDoc.title} className='kycDocModal__image' />
                    )}
                </div>
            </Modal>

            <Modal open={uploadModalOpen} onClose={closeUploadModal} maxWidth={480}>
                <form className='kycDocModal' onSubmit={handleUploadSubmit}>
                    <div className='kycDocModal__title'>{editDoc ? 'Update Document' : 'Upload Document'}</div>

                    <div className='profileEditModal__field'>
                        <label className='profileEditModal__label'>Document Type *</label>
                        <select
                            className='profileEditModal__input'
                            value={uploadForm.document_type_id}
                            onChange={handleUploadFieldChange('document_type_id')}
                            required
                        >
                            <option value='' disabled>Select document type</option>
                            {documentTypes.map((type) => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className='profileEditModal__field mt-3'>
                        <label className='profileEditModal__label'>File {editDoc ? '(leave empty to keep current file)' : '*'}</label>
                        <input
                            type='file'
                            accept='image/*,.pdf'
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            required={!editDoc}
                        />
                    </div>

                    <div className='profileEditModal__field mt-3'>
                        <label className='profileEditModal__label'>Document Number</label>
                        <input
                            type='text'
                            className='profileEditModal__input'
                            placeholder='document_number'
                            maxLength={128}
                            value={uploadForm.document_number}
                            onChange={handleUploadFieldChange('document_number')}
                        />
                    </div>

                    <div className='profileEditModal__field mt-3'>
                        <label className='profileEditModal__label'>Notes</label>
                        <input
                            type='text'
                            className='profileEditModal__input'
                            placeholder='notes'
                            value={uploadForm.notes}
                            onChange={handleUploadFieldChange('notes')}
                        />
                    </div>

                    <div className='profileEditModal__actions'>
                        <button type='button' className='commonBtnTransparent' onClick={closeUploadModal} disabled={uploading}>
                            Cancel
                        </button>
                        <button type='submit' className='commonBtn' disabled={uploading}>
                            {uploading ? (editDoc ? 'Updating...' : 'Uploading...') : (editDoc ? 'Update' : 'Upload')}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal open={!!deleteDoc} onClose={() => { if (!deleting) setDeleteDoc(null) }} maxWidth={380}>
                <div className='logoutConfirm'>
                    <div className='logoutConfirm__title'>Delete document?</div>
                    <p className='logoutConfirm__desc'>
                        Are you sure you want to delete {deleteDoc?.title}? This action cannot be undone.
                    </p>
                    <div className='logoutConfirm__actions'>
                        <button
                            type='button'
                            className='commonBtnTransparent'
                            onClick={() => setDeleteDoc(null)}
                            disabled={deleting}
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className='commonBtn'
                            onClick={handleDeleteConfirm}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default KycDocument
